import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useIdeasNodeStore } from "../../stores/ideaNodesStore";
import { timeStringToSeconds } from "./TimeReference";
import YouTube, { YouTubePlayer } from "./yt_inner/YouTubeEmbedInner";
import SegmentProgressBar from "./yt_inner/YTSegmentProgressBar";
import { css } from "@emotion/css";
import { useVisibility } from "../../hooks/useVisibility";
import { useYTVideoPlayerStore } from "../../stores/ytVideoPlayerStore";
import React from "react";

const THUMBNAIL_BASE_URL = "https://i.ytimg.com/vi";
const THUMBNAIL_QUALITY = "hqdefault";
const VISIBILITY_DEBOUNCE = 20;
const PRELOAD_THRESHOLD = 1.5;
const MAX_RETRIES = 5;
const RETRY_DELAY = 1000;

const ENABLE_AUTO_PLAY_PAUSE_FRAME_LOADING_SEQUENCE = true;

interface PlayerState {
  player: YouTubePlayer | null;
  isReady: boolean;
  hasError: boolean;
  retryCount: number;
  lastOperation: string | null;
  isPlaying: boolean;
  hasInitialized: boolean;
  shouldPlayAfterMount: boolean;
}

// Base button styles shared by all buttons
const baseButtonStyles = css`
  position: absolute;
  background: rgba(33, 33, 33, 0.9);
  border: none;
  color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border: 3px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  opacity: 0.9;
  aspect-ratio: 1 / 1; /* Force perfect circle */
  box-sizing: border-box; /* Include border in width/height calculations */

  &:hover {
    background: rgba(33, 33, 33, 0.95);
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5);
    opacity: 1;
  }

  svg {
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
  }
`;

// Standard play button style (centered)
const playButtonStyles = css`
  ${baseButtonStyles}
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 72px;
  height: 72px;

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
  }

  &:active {
    transform: translate(-50%, -50%) scale(0.95);
  }

  svg {
    width: 36px;
    height: 36px;
  }
`;

// Dual button layout container
const dualButtonContainerStyles = css`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 99%;
  height: 80%;
  display: flex;
  justify-content: center;
  align-items: center; /* Center items vertically */
  gap: 40px;
  z-index: 20;
  // background: rgba(0, 0, 0, 0.8); /* Black semi-transparent background */
  background: transparent;
  padding: 15px 20px;
`;

// Button container with label
const buttonContainerStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: auto; /* Let height adjust naturally */
`;

// Replay button style
const replayButtonStyles = css`
  ${baseButtonStyles}
  width: 64px;
  height: 64px;
  z-index: 30;
  position: static; /* Override absolute positioning from baseButtonStyles */
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  aspect-ratio: 1 / 1;

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 32px;
    height: 32px;
  }
`;

// Continue button style
const continueButtonStyles = css`
  ${baseButtonStyles}
  width: 64px;
  height: 64px;
  background: rgba(33, 150, 33, 0.9);
  z-index: 30;
  position: static; /* Override absolute positioning from baseButtonStyles */
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  aspect-ratio: 1 / 1;

  &:hover {
    transform: scale(1.1);
    background: rgba(33, 150, 33, 0.95);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 32px;
    height: 32px;
  }
`;

// Outlined panel to wrap the two buttons with a prominent close button
const dualButtonOutlineStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding: 10px 14px; /* Tight padding */
  border: 2px solid rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.65);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6);
`;

const outlineCloseButtonStyles = css`
  position: absolute;
  top: -12px; /* Slightly outside for prominence */
  right: -12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e53935; /* Visible red */
  border: 2px solid #ffffff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.8);
  transition: transform 0.15s ease, background 0.15s ease;

  &:hover {
    transform: scale(1.07);
    background: #d32f2f;
  }

  &:active {
    transform: scale(0.96);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const labelStyles = css`
  font-size: 14px;
  font-weight: 500;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  max-width: 80px; /* Limit width to control wrapping */
  text-align: center; /* Center the text */
  min-height: 40px; /* Ensure space for two lines */
`;

// Overlay Controls Component
interface YTVideoCustomOverlayControlsProps {
  isNearEndTime: boolean;
  player: YouTubePlayer | null;
  playVideoSafely: (player: YouTubePlayer | null) => Promise<void>;
  playerId: string;
  clearCurrentlyPlaying: (id: string) => void;
  setContinuedFromTime: React.Dispatch<React.SetStateAction<number | null>>;
  endTimeInSeconds: number;
  setUseYouTubeControls: React.Dispatch<React.SetStateAction<boolean>>;
  setMountKey: React.Dispatch<React.SetStateAction<number>>;
  overlayStyles: string; // Keep this one as it depends on isPlaying state
}

const YTVideoCustomOverlayControls: React.FC<
  YTVideoCustomOverlayControlsProps
> = ({
  isNearEndTime,
  player,
  playVideoSafely,
  playerId,
  clearCurrentlyPlaying,
  setContinuedFromTime,
  endTimeInSeconds,
  setUseYouTubeControls,
  setMountKey,
  overlayStyles,
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  // Reset dismissal when near-end state turns off so it can appear on the next natural trigger
  useEffect(() => {
    if (!isNearEndTime) {
      setIsDismissed(false);
    }
  }, [isNearEndTime]);
  return (
    <div
      className={overlayStyles}
      style={{ pointerEvents: !isNearEndTime || isDismissed ? "none" : "auto" }}
    >
      {!isNearEndTime || isDismissed ? (
        <></>
      ) : (
        // IMPORTANT: We used to show our own button, but we are now opting to show YouTube's own controls
        // so it aligns better with what youtube most likely counts as user initiated plays
        // and be more favorable towards our fair use case
        //
        // Standard play button when not near end time
        // <button
        //   className={playButtonStyles}
        //   onClick={async (e) => {
        //     e.stopPropagation();
        //     // Use the safe play method that handles global state
        //     await playVideoSafely(player);
        //   }}
        //   title="Play"
        // >
        //   <svg viewBox="0 0 24 24" fill="currentColor">
        //     <path d="M8 5v14l11-7z" />
        //   </svg>
        // </button>
        // Dual buttons when near end time
        <div className={dualButtonContainerStyles}>
          <div className={dualButtonOutlineStyles}>
            {/* Close (X) button */}
            <button
              className={outlineCloseButtonStyles}
              onClick={(e) => {
                e.stopPropagation();
                setIsDismissed(true);
              }}
              title="Hide options"
              aria-label="Hide options"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.9a1 1 0 0 0 1.41-1.41L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4z" />
              </svg>
            </button>

            {/* Replay segment button */}
            <div className={buttonContainerStyles}>
              <button
                className={replayButtonStyles}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    // Also use the safe play method that handles global state
                    await playVideoSafely(player, true);
                    // Force UI update by updating the mount key
                    setMountKey((prev) => prev + 1);
                  } catch (error) {
                    console.error("Error replaying video:", error);
                  }
                }}
                title="Replay segment"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
                </svg>
              </button>
              <div className={labelStyles}>Replay</div>
            </div>

            {/* Continue playing button */}
            <div className={buttonContainerStyles}>
              <button
                className={continueButtonStyles}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (player) {
                    try {
                      // Get current time before doing anything else
                      const currentTime = await player.getCurrentTime();
                      console.log(
                        "Current time before continuing:",
                        currentTime
                      );

                      // Clear from global state before proceeding
                      clearCurrentlyPlaying(playerId);

                      // First set the timestamp for continuation
                      // Using the exact time for better precision
                      setContinuedFromTime(currentTime);

                      // Use requestAnimationFrame to ensure we're in sync with the browser's rendering cycle
                      // This creates more reliable timing than setTimeout
                      requestAnimationFrame(() => {
                        // Now enable YouTube controls - this will be picked up by our useEffect
                        setUseYouTubeControls(true);
                      });

                      // Note: The actual remounting happens in useEffect in the main component
                      // This is a more React-aligned approach than using setTimeout
                    } catch (error) {
                      console.error("Error continuing video:", error);
                      // Fallback if we can't get current time
                      setContinuedFromTime(endTimeInSeconds);
                      clearCurrentlyPlaying(playerId);
                      setUseYouTubeControls(true);
                    }
                  }
                }}
                title="Continue playing"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
              <div className={labelStyles}>Keep watching</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const YTVideoEmbed = React.memo(
  (props: {
    startTime: string;
    endTime: string;
    engagementScore?: string;
    videoId?: string;
  }) => {
    // Early return if startTime is null or undefined
    if (!props.startTime) {
      // console.log('Skipping YTVideoEmbed render - startTime is null or undefined');
      return null;
    }

    // Track component renders with useRef and useEffect (for debugging / logging purposes)
    // const renderCountRef = useRef(0);

    // // This will run after each render and indicate if it's the first render or a re-render
    // useEffect(() => {
    //   renderCountRef.current += 1;
    //   console.log(`[IN] YTVideoEmbed render #${renderCountRef.current} - startTime: ${props.startTime}`, {
    //     isFirstRender: renderCountRef.current === 1,
    //     props
    //   });
    // });

    const sessionMetadata = useIdeasNodeStore((state) => state.sessionMetadata);
    const ytVideoId = props.videoId || sessionMetadata?.ytVideoId;
    const startTimeInSeconds = timeStringToSeconds(String(props.startTime));
    const endTimeInSeconds = timeStringToSeconds(String(props.endTime));

    // Visibility tracking
    const { elementRef, isVisible } = useVisibility({
      viewportThreshold: PRELOAD_THRESHOLD,
      debounceMs: VISIBILITY_DEBOUNCE,
    });

    // State management
    const [showError, setShowError] = useState(false);
    const [mountKey, setMountKey] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPlayerReady, setIsPlayerReady] = useState(false);
    const [isNearEndTime, setIsNearEndTime] = useState(false);
    const [useYouTubeControls, setUseYouTubeControls] = useState(false);
    const [continuedFromTime, setContinuedFromTime] = useState<number | null>(
      null
    );
    // State to trigger a re-render once the player has finished its initial setup
    const [hasInitializedRender, setHasInitializedRender] = useState(false);
    const playerStateRef = useRef<PlayerState>({
      player: null,
      isReady: false,
      hasError: false,
      retryCount: 0,
      lastOperation: null,
      isPlaying: false,
      hasInitialized: false,
      shouldPlayAfterMount: false,
    });

    // Stable unique suffix for this iframe's DOM id to avoid collisions with
    // other identical segments across the page, which can cause API confusion
    // and sporadic re-initializations that appear as resize "breathing".
    const uniqueIframeIdRef = useRef<string>("");
    if (!uniqueIframeIdRef.current) {
      uniqueIframeIdRef.current = `inst-${Math.random().toString(36).slice(2, 10)}`;
    }

    // Safe player operations with retries
    const safePlayerOp = useCallback(
      async (
        operation: (player: YouTubePlayer) => Promise<any>,
        operationName: string,
        maxRetries: number = MAX_RETRIES
      ): Promise<boolean> => {
        const { player } = playerStateRef.current;
        if (!player) return false;

        // Don't retry the same failed operation too quickly
        if (
          playerStateRef.current.lastOperation === operationName &&
          playerStateRef.current.retryCount >= maxRetries
        ) {
          console.warn(`Operation ${operationName} failed too many times`);
          return false;
        }

        try {
          await operation(player);
          // Reset retry count on success
          if (playerStateRef.current.lastOperation === operationName) {
            playerStateRef.current.retryCount = 0;
            playerStateRef.current.lastOperation = null;
          }
          return true;
        } catch (e) {
          console.warn(`Player operation ${operationName} failed:`, e);

          // Update retry state
          if (playerStateRef.current.lastOperation === operationName) {
            playerStateRef.current.retryCount++;
          } else {
            playerStateRef.current.lastOperation = operationName;
            playerStateRef.current.retryCount = 1;
          }

          // Retry after delay if we haven't exceeded max retries
          if (playerStateRef.current.retryCount < maxRetries) {
            setTimeout(() => {
              safePlayerOp(operation, operationName, maxRetries);
            }, RETRY_DELAY * playerStateRef.current.retryCount);
          }

          return false;
        }
      },
      []
    );

    // Initialize player
    const initializePlayer = useCallback(
      async (player: YouTubePlayer) => {
        if (ENABLE_AUTO_PLAY_PAUSE_FRAME_LOADING_SEQUENCE) {
          await safePlayerOp(async (p) => await p.mute(), "mute");

          await safePlayerOp(
            async (p) => await p.seekTo(startTimeInSeconds, true),
            "seekTo"
          );
        } else {
          playerStateRef.current.hasInitialized = true;
          setHasInitializedRender(true);
        }
      },
      [safePlayerOp, startTimeInSeconds]
    );

    // Check if time is within bounds
    const checkTimeAndReset = useCallback(
      async (player: YouTubePlayer) => {
        try {
          const currentTime = await player.getCurrentTime();

          // Check if we're near the end time (within 1.5 seconds or past it)
          const isNearEnd =
            endTimeInSeconds - currentTime <= 1.5 ||
            currentTime >= endTimeInSeconds;
          setIsNearEndTime(isNearEnd);

          // Don't reset player time if we're in YouTube controls mode
          if (useYouTubeControls) {
            return; // Let the video continue playing beyond the end time
          }

          // Reset only if not using YouTube controls
          if (currentTime >= endTimeInSeconds) {
            await player.seekTo(startTimeInSeconds, true);
            await player.pauseVideo();
          } else if (currentTime < startTimeInSeconds) {
            await player.seekTo(startTimeInSeconds, true);
            // Don't pause, let it continue playing from the correct start time
          }
        } catch (e) {
          console.warn("Error checking time:", e);
        }
      },
      [startTimeInSeconds, endTimeInSeconds, useYouTubeControls]
    );

    // Get store methods for managing global playback state
    const { setCurrentlyPlaying, clearCurrentlyPlaying } =
      useYTVideoPlayerStore();
    
    // Create a unique ID for this player instance
    const playerId = useMemo(
      () => `yt-${ytVideoId}-${startTimeInSeconds}-${endTimeInSeconds}`,
      [ytVideoId, startTimeInSeconds, endTimeInSeconds]
    );
    // Encapsulated method to play a video safely with global state management
    // skipEndCheck parameter can be used when we've already checked position in the calling function
    const playVideoSafely = useCallback(
      async (player: YouTubePlayer, skipEndCheck = false) => {
        if (!player) return;

        try {
          // Skip the end position check if requested (e.g., when called from progress bar)
          // This prevents race conditions where getCurrentTime() hasn't yet caught up with a recent seek
          if (!skipEndCheck) {
            // Only perform the check and potential seek if not skipped
            const currentTime = await player.getCurrentTime();
            if (currentTime >= endTimeInSeconds && !useYouTubeControls) {
              // If time is beyond end, go back to start (but only if not in YouTube controls mode)
              await player.seekTo(startTimeInSeconds, true);
              console.log("Seeking to start time:", startTimeInSeconds);
            }
          }

          // Set as current playing video (pauses other videos)
          await setCurrentlyPlaying(playerId, player);

          // Now play this video
          await player.playVideo();
        } catch (error) {
          console.error("Error playing video:", error);
        }
      },
      [playerId, setCurrentlyPlaying, startTimeInSeconds, endTimeInSeconds]
    );

    // Handle player ready event
    const handlePlayerReady = useCallback(
      (event: any) => {
        const player = event.target as YouTubePlayer;
        const shouldPlay = playerStateRef.current.shouldPlayAfterMount;

        // Update the player state reference
        playerStateRef.current.player = player;
        playerStateRef.current.isReady = true;
        playerStateRef.current.shouldPlayAfterMount = false; // Reset flag after reading it

        setShowError(false);
        setIsPlayerReady(true);

        // If we're in YouTube controls mode and have a continued time, we need to explicitly play the video
        if (useYouTubeControls && continuedFromTime !== null) {
          console.log(
            "YouTube controls mode ready with start time:",
            continuedFromTime
          );
          // Mark as initialized to prevent other initialization logic
          playerStateRef.current.hasInitialized = true;

          // Create a promise-based sequence of operations
          const initYouTubeControlsMode = async (player: YouTubePlayer) => {
            try {
              // First, set up event listeners for more reliable state tracking
              const readyState = await new Promise<boolean>((resolve) => {
                // Check if player is already in ready state
                if (player.getPlayerState && player.getPlayerState() !== -1) {
                  resolve(true);
                  return;
                }

                // Set up one-time event listener for player ready state
                const onReadyStateChange = (event: any) => {
                  if (event.data !== -1) {
                    // Not unstarted
                    player.removeEventListener(
                      "onStateChange",
                      onReadyStateChange
                    );
                    resolve(true);
                  }
                };

                // Add listener and also set a safety timeout
                player.addEventListener("onStateChange", onReadyStateChange);
                // Safety fallback - resolve after 500ms regardless
                setTimeout(() => resolve(true), 500);
              });

              // THE SEQUENCE BELOW DOES A PLAY->PAUSE SEQUENCE TO LOAD THE TARGET FRAME
              // BUT, THAT'S FAIRLY GRAY AREA REGARDING YT PLAYER POLICIES LIKE
              // AUTOPLAY POTENTIALLY NOT COUNTING AS A VIEW TOWARDS THE AUTHOR [although maybe it isn't an issue with approaches 2 and certainly 3].
              // WE HAVE 3 OPTIONS:
              // 1. HAVE THE AUTO-PLAY-STOP SEQUENCE AND HAVE A CUSTOM OVERLAY PLAY BUTTON, HAVING THE OVERLAY SEEMS TO BE ALLOWED, BUT DOES NOT
              // COUNT AS A VIEW TOWARDS THE AUTHOR (this has been commented out inside the YTVideoCustomOverlayControls).
              // 2. HAVE THE AUTO-PLAY-STOP SEQUENCE BUT DON'T HAVE A CUSTOM OVERLAY AND MAKE THE USER TAP THE PLAYER [so it interacts with the native yt play]; HOWEVER, THE
              // FIRST TAP BRINGS UP THE CONTORLLERS (AT LEAST ON MOBILE) AND ONLY THE SECOND TAP (ON THE NOW VISIBLE NATIVE PLAY BUTTON)
              // WOULD PLAY THE BUTTON, CREATING A POTENTIALLY CONFUSING USER EXPERIENCE [but the play has a good chance of counting favorably towards the video author].
              // 3. SHOW THE NATIVE/ORIGINAL YT PLAY BUTTON WITHOUT THE AUTO-PLAY-PAUSE SEQUENCE.
              // THIS IS A BIT UGLY UI/UX WISE, BUT CAN BE MITIGATED BY HIDING/COLLAPSING PLAYERS
              // AND ONLY SHOWING THEM ONDEMAND. ON THE UPSIDE THIS IS THE MOST VANILLA / LIKELY YT ACCEPTED APPROACH.

              if (ENABLE_AUTO_PLAY_PAUSE_FRAME_LOADING_SEQUENCE) {
                // Now we can be more confident the player is initialized
                if (readyState) {
                  // Make sure we're at the right position first
                  await player.seekTo(continuedFromTime, true);

                  // Clear from global state first to avoid issues
                  const uniqueId = `${playerId}-controls`;
                  clearCurrentlyPlaying(uniqueId);

                  // Now set as current playing and play
                  await setCurrentlyPlaying(uniqueId, player);

                  // For autoplay concerns, first mute, then play, then unmute
                  // This is more reliable on mobile browsers that block autoplay with sound
                  await player.mute();
                  await player.playVideo();

                  // After playback has started, try to unmute
                  const playbackStarted = await new Promise<boolean>(
                    (resolve) => {
                      const checkPlaying = async () => {
                        const state = await player.getPlayerState();
                        if (state === 1) {
                          // YT.PlayerState.PLAYING
                          resolve(true);
                        } else {
                          requestAnimationFrame(checkPlaying);
                        }
                      };
                      checkPlaying();
                      // Safety fallback
                      setTimeout(() => resolve(false), 1500);
                    }
                  );

                  if (playbackStarted) {
                    // Try to unmute now that playback has started
                    await player.unMute();
                    playerStateRef.current.isPlaying = true;
                    setIsPlaying(true);
                    console.log("Started playback in YouTube controls mode");
                  }
                }
              }
            } catch (e) {
              console.warn("Error initiating YouTube controls mode:", e);
            }
          };

          // Execute the sequence
          safePlayerOp(initYouTubeControlsMode, "ytControlsAutoPlay");
        } else if (shouldPlay) {
          // If the shouldPlayAfterMount flag is set, ensure we play the video
          console.log(
            "Player ready - shouldPlayAfterMount flag is set, ensuring playback continues"
          );

          // Use a reliable promise-based approach
          const ensurePlayback = async (player: YouTubePlayer) => {
            try {
              // Check player state first
              const state = await player.getPlayerState();
              if (state !== 1) {
                // Not playing
                await player.playVideo();

                // Verify playback actually started
                await new Promise<void>((resolve) => {
                  const checkPlaying = async () => {
                    const currentState = await player.getPlayerState();
                    if (currentState === 1) {
                      // YT.PlayerState.PLAYING
                      resolve();
                    } else {
                      requestAnimationFrame(checkPlaying);
                    }
                  };
                  checkPlaying();
                  // Safety fallback
                  setTimeout(() => resolve(), 1000);
                });
              }

              // Update state tracking
              playerStateRef.current.isPlaying = true;
              setIsPlaying(true);
            } catch (e) {
              console.warn("Error continuing playback after remount:", e);
            }
          };

          // Execute the sequence
          safePlayerOp(ensurePlayback, "continuePlayback");
        } else {
          // Regular initialization for normal mode
          initializePlayer(player).catch((error) => {
            console.error("Player initialization failed:", error);
            handleError(error);
          });
        }
      },
      [
        initializePlayer,
        useYouTubeControls,
        continuedFromTime,
        safePlayerOp,
        playerId,
        setCurrentlyPlaying,
        clearCurrentlyPlaying,
      ]
    );

    // Handle time update
    const handleTimeUpdate = useCallback(
      (currentTime: number) => {
        const { player } = playerStateRef.current;
        if (!player) return;

        // Check if the requested time is more than 1.5 seconds away from the end
        const isWellBeforeEnd = endTimeInSeconds - currentTime > 1.5;

        // Only reset isNearEndTime if:
        // 1. We are currently in near-end-time state AND
        // 2. The user has dragged to a position well before the end
        const wasNearEnd = isNearEndTime;
        if (wasNearEnd && isWellBeforeEnd) {
          setIsNearEndTime(false);
        }

        safePlayerOp(async (p) => {
          // Always seek to the requested time
          await p.seekTo(currentTime, true);

          // Only play the video if we were coming from isNearEndTime=true
          // This means the user was previously seeing the dual-button interface
          // and probably wants the video to resume playing after seeking
          if (wasNearEnd) {
            // Skip the end check since we've already seeked to the correct position
            await playVideoSafely(p, true);
          }
        }, "seekToTime");
      },
      [safePlayerOp, isNearEndTime, setIsNearEndTime, playVideoSafely]
    );

    // Handle player state changes
    const handleStateChange = useCallback(
      (event: any) => {
        const player = event.target as YouTubePlayer;
        if (!player) return;

        const state = event.data;

        // GUARD: Ignore spurious UNSTARTED transitions after initialization
        // This may happen(?) during streaming DOM updates when the iframe is briefly detached/reattached
        if (state === -1 && playerStateRef.current.hasInitialized) return;

        const isNowPlaying = state === 1;

        // Update local playing state, but don't set isPlaying to true during initialization
        // to prevent triggering the useEffect that registers with global store
        // this if (shouldUpdatePlayingState) condition seems to make clicking on play more reliable when another video is already playing
        const shouldUpdatePlayingState = isNowPlaying
          ? playerStateRef.current.hasInitialized
          : true;

        playerStateRef.current.isPlaying = isNowPlaying;
        if (shouldUpdatePlayingState) {
          setIsPlaying(isNowPlaying);
        }

        // Only handle pausing case in the state change handler
        // (Play actions are handled directly in click handlers)
        if (state === 2 && playerStateRef.current.hasInitialized) {
          // Video was paused, clear from global state if it's the current one
          clearCurrentlyPlaying(playerId);
        }

        // -1 = unstarted
        // 0 = ended
        // 1 = playing
        // 2 = paused
        // 3 = buffering
        // 5 = video cued

        if (state === -1 || state === 5) {
          // Video is cued/ready to play. Avoid unintended autoplay which pauses other videos.
          // if (playerStateRef.current.shouldPlayAfterMount) {
          // console.log('Will play video id ' + playerId);
          safePlayerOp(async (p) => {
            try {
              await p.playVideo();
            } catch (e) {
              console.warn("Error playing video:", e);
            }
          }, "playVideo");
        } else if (state === 1) {
          if (
            !playerStateRef.current.hasInitialized &&
            ENABLE_AUTO_PLAY_PAUSE_FRAME_LOADING_SEQUENCE
          ) {
            // Initial play - pause and seek (don't register with global store during init)
            safePlayerOp(async (p) => {
              try {
                const currentTime = await p.getCurrentTime();
                if (Math.abs(currentTime - startTimeInSeconds) > 0.5) {
                  await p.seekTo(startTimeInSeconds, true);
                }
                await p.pauseVideo();
                playerStateRef.current.hasInitialized = true;
                setHasInitializedRender(true);
                // Ensure isPlaying is set to false after initialization to show overlay controls
                setIsPlaying(false);
              } catch (e) {
                console.warn("Error initializing video:", e);
              }
            }, "initialPauseAndSeek");
          } else {
            // Regular play - unmute and check time
            safePlayerOp(async (p) => {
              try {
                await p.unMute();
                await checkTimeAndReset(p);
              } catch (e) {
                console.warn("Error during play:", e);
              }
            }, "playSetup");

            // Set up interval to check time while playing
            const timeCheckInterval = setInterval(() => {
              if (playerStateRef.current.isPlaying && player) {
                checkTimeAndReset(player);
              }
            }, 1000);

            // Clean up interval when paused
            const clearCheck = () => {
              if (timeCheckInterval) {
                clearInterval(timeCheckInterval);
              }
            };

            player.addEventListener("onStateChange", (e: any) => {
              if (e.data !== 1) {
                // Not playing
                clearCheck();
              }
            });

            // Cleanup on component unmount
            return clearCheck;
          }
        }
      },
      [safePlayerOp, startTimeInSeconds, checkTimeAndReset]
    );

    // Handle player errors
    const handleError = useCallback((error: any) => {
      console.error("YouTube player error:", error);

      // Only handle fatal errors (2=invalid parameter, 5=HTML5 error, 100=video not found, 101/150=embedding disabled)
      const fatalErrors = [2, 5, 100, 101, 150];
      if (error?.data && fatalErrors.includes(error.data)) {
        playerStateRef.current.hasError = true;
        setShowError(true);

        // Reset player state
        playerStateRef.current.player = null;
        playerStateRef.current.isReady = false;

        // Only remount if we've exceeded retry count or it's a fatal error
        if (playerStateRef.current.retryCount >= MAX_RETRIES) {
          // Reset retry state
          playerStateRef.current.retryCount = 0;
          playerStateRef.current.lastOperation = null;

          // Remount after a delay
          setTimeout(() => {
            setMountKey((prev) => prev + 1);
          }, RETRY_DELAY * 2);
        }
      } else {
        // For non-fatal errors, just log and continue
        console.warn("Non-fatal YouTube player error:", error);
      }
    }, []);

    // Handle visibility changes
    useEffect(() => {
      if (!isVisible && !isPlaying) {
        // Reset all state when hidden
        playerStateRef.current = {
          player: null,
          isReady: false,
          hasError: false,
          retryCount: 0,
          lastOperation: null,
          isPlaying: false,
          hasInitialized: false,
        };
        setShowError(false);
        setHasInitializedRender(false);
      }
    }, [isVisible, isPlaying]);

    // Clean up on unmount
    useEffect(() => {
      return () => {
        const { player } = playerStateRef.current;
        if (player) {
          try {
            // Clear from global state if it's the current playing video
            // Use the appropriate ID based on mode
            const uniqueId = useYouTubeControls
              ? `${playerId}-controls`
              : playerId;
            clearCurrentlyPlaying(uniqueId);
            player.destroy();
          } catch (e) {
            console.warn("Error destroying player:", e);
          }
        }
      };
    }, [playerId, clearCurrentlyPlaying, useYouTubeControls]);

    // Track play/pause state changes and update the YT video store
    // This ensures consistent behavior across the app, even in YouTube controls mode
    useEffect(() => {
      const { player } = playerStateRef.current;
      // Only register after initial setup is finished to prevent spurious pauses
      if (isPlaying && player && hasInitializedRender) {
        // When this player starts playing, register it with the global store
        // Use a unique ID for YouTube controls mode to avoid confusion with segment mode
        const uniqueId = useYouTubeControls ? `${playerId}-controls` : playerId;
        setCurrentlyPlaying(uniqueId, player).catch((error) =>
          console.warn("Error registering player with global store:", error)
        );
      }
    }, [
      isPlaying,
      playerId,
      useYouTubeControls,
      setCurrentlyPlaying,
      hasInitializedRender,
    ]);

    // Handle YouTube controls mode transition
    // This effect triggers the remount when YouTube controls are enabled
    useEffect(() => {
      // Only run this effect when YouTube controls are enabled and we have a continued time
      if (useYouTubeControls && continuedFromTime !== null) {
        // Use requestAnimationFrame to sync with browser rendering cycle
        const frameId = requestAnimationFrame(() => {
          // Increment mount key to force remount with new options
          setMountKey((prev) => prev + 1);

          // Set a flag to ensure we play after remount
          playerStateRef.current.shouldPlayAfterMount = true;
        });

        // Cleanup function
        return () => {
          // Cancel animation frame if component unmounts during transition
          cancelAnimationFrame(frameId);
        };
      }
    }, [useYouTubeControls, continuedFromTime, setMountKey]);

    // YouTube player options
    const options = useMemo(() => {
      // In YouTube controls mode, explicitly set autoplay to 1
      const playerConfig = {
        playerVars: {
          playsinline: 1,
          start: useYouTubeControls
            ? continuedFromTime
              ? Math.floor(continuedFromTime)
              : endTimeInSeconds
            : startTimeInSeconds,
          end: useYouTubeControls ? undefined : endTimeInSeconds,
          controls: useYouTubeControls ? 1 : 0,
          autoplay: useYouTubeControls ? 1 : 0, // Auto-play when continuing
          rel: 0,
          modestbranding: 1,
          enablejsapi: 1,
          origin: window.location.origin,
          widget_referrer: window.location.href,
          fs: 1,
          iv_load_policy: 3,
          showinfo: 0,
          loading: "eager",
          host: "https://www.youtube.com",
          cc_load_policy: 1, // Force closed captions to be shown by default
        },
      };

      console.log(
        "Creating YouTube player with options:",
        useYouTubeControls ? "YouTube controls enabled" : "Custom controls",
        "start time:",
        playerConfig.playerVars.start,
        "autoplay:",
        playerConfig.playerVars.autoplay
      );

      return playerConfig;
    }, [
      startTimeInSeconds,
      endTimeInSeconds,
      useYouTubeControls,
      continuedFromTime,
    ]);

    // Styles
    const containerStyles = useMemo(
      () =>
        css({
          position: "relative",
          width: "100%",
          margin: "5px auto 0 auto",
          marginBottom: "0.9rem",
          zIndex: 0,
          boxSizing: "border-box", // This may or may not be needed for fixing the yt player breathing problem
          // overflowX: "auto", // This may or may not be needed for fixing the yt player breathing problem
        }),
      []
    );

    const videoContainerStyles = useMemo(
      () =>
        css({
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          height: "280px", // Fixed height matching YouTube iframe height
          backgroundColor: "#000",
          cursor: "pointer",
          boxSizing: "border-box", // This may or may not be needed for fixing the yt player breathing problem
        }),
      []
    );

    // This is invisible during playback to let clicks reach the YouTube iframe
    const overlayStyles = css`
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 5;
      pointer-events: ${isPlaying || !isNearEndTime ? "none" : "auto"};
      background: ${isPlaying ? "transparent" : "rgba(0, 0, 0, 0.2)"};
      opacity: ${isPlaying ? 0 : 1};
      transition: opacity 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Base button styles shared by all buttons
    const baseButtonStyles = css`
      position: absolute;
      background: rgba(33, 33, 33, 0.9);
      border: none;
      color: white;
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      border: 3px solid rgba(255, 255, 255, 0.3);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      opacity: ${isHovered ? 1 : 0.9};
      aspect-ratio: 1 / 1; /* Force perfect circle */
      box-sizing: border-box; /* Include border in width/height calculations */

      &:hover {
        background: rgba(33, 33, 33, 0.95);
        border-color: rgba(255, 255, 255, 0.5);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5);
        opacity: 1;
      }

      svg {
        filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
      }
    `;

    // Standard play button style (centered)
    const playButtonStyles = css`
      ${baseButtonStyles}
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 72px;
      height: 72px;

      &:hover {
        transform: translate(-50%, -50%) scale(1.1);
      }

      &:active {
        transform: translate(-50%, -50%) scale(0.95);
      }

      svg {
        width: 36px;
        height: 36px;
      }
    `;

    const thumbnailStyles = useMemo(
      () => css`
        @keyframes pulse {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        height: 280px;
        width: 100%;
        background: linear-gradient(
          -90deg,
          #1a1a1a 0%,
          #2a2a2a 50%,
          #1a1a1a 100%
        );
        background-size: 400% 400%;
        animation: pulse 2s ease-in-out infinite;
        background-position: center;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        position: relative;
        ${showError
          ? `
        &::before {
          content: "Loading video";
          position: absolute;
          z-index: 1;
          color: #fff;
          background: rgba(0, 0, 0, 0.7);
          padding: 8px 16px;
          border-radius: 4px;
          pointer-events: none;
        }
      `
          : ""}
      `,
      [ytVideoId, showError]
    );

    // Ensure the actual YouTube iframe is fully responsive and sized via CSS only
    const iframeResponsiveStyles = useMemo(
      () => css`
        width: 100%;
        height: 100%;
        display: block;
        border: 0;
      `,
      []
    );

    const { player } = playerStateRef.current;

    return (
      <div
        ref={elementRef}
        className={`ytFrameTopContainer ${containerStyles}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isVisible || isPlaying ? (
          <div className={videoContainerStyles}>
            <YouTube
              key={mountKey}
              videoId={ytVideoId}
              id={`yt-${ytVideoId}-${startTimeInSeconds}-${uniqueIframeIdRef.current}`}
              opts={options}
              onReady={handlePlayerReady}
              onStateChange={handleStateChange}
              onError={handleError}
              className={thumbnailStyles}
              iframeClassName={iframeResponsiveStyles}
            />
            {isPlayerReady &&
              !isPlaying &&
              !useYouTubeControls &&
              hasInitializedRender && (
                <YTVideoCustomOverlayControls
                  isNearEndTime={isNearEndTime}
                  player={playerStateRef.current.player}
                  playVideoSafely={playVideoSafely}
                  playerId={playerId}
                  clearCurrentlyPlaying={clearCurrentlyPlaying}
                  setContinuedFromTime={setContinuedFromTime}
                  endTimeInSeconds={endTimeInSeconds}
                  setUseYouTubeControls={setUseYouTubeControls}
                  setMountKey={setMountKey}
                  overlayStyles={overlayStyles}
                />
              )}
            {/* Only include progress bar when not using YouTube controls */}
            {!useYouTubeControls && (
              <div
                className="progress-bar-isolator"
                style={{
                  position: "relative",
                  zIndex: 100,
                }}
                // We only need stopPropagation on parent container
                // to prevent YtSharp from handling these events
                onClick={(e) => {
                  e.stopPropagation();
                }}
                // Only touch events need special handling
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
              >
                <SegmentProgressBar
                  youtubePlayer={playerStateRef.current.player}
                  startTimeInSeconds={startTimeInSeconds}
                  endTimeInSeconds={endTimeInSeconds}
                  onTimeUpdate={handleTimeUpdate}
                  isHovered={isHovered}
                />
              </div>
            )}
          </div>
        ) : (
          <div className={thumbnailStyles}></div>
        )}
      </div>
    );
  }
);
